import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { Palette } from '@/types/palette';

interface PaletteState {
  palettes: Palette[];
  isLoading: boolean;
  error: string | null;
  isInitialized: boolean;
  locale: string | null;
}

interface PaletteActions {
  setPalettes: (palettes: Palette[]) => void;
  getPaletteById: (id: string | number) => Palette | undefined;
  getPaletteColors: (id: string | number) => string[] | undefined;
  reset: () => void;
}

type PaletteStore = PaletteState & PaletteActions;

const initialState: PaletteState = {
  palettes: [],
  isLoading: false,
  error: null,
  isInitialized: false,
  locale: null,
};

export const usePaletteStore = create<PaletteStore>()(
  subscribeWithSelector(
    immer((set, get) => ({
      ...initialState,

      setPalettes: (palettes) => {
        set((state) => {
          // Sort palettes once when setting them
          state.palettes = [...palettes].sort(
            (a, b) =>
              (a.attributes.sort_order ?? 999) -
              (b.attributes.sort_order ?? 999)
          );
        });
      },

      getPaletteById: (id) => {
        const { palettes } = get();
        const idStr = String(id);
        return palettes.find((p) => p.id === idStr);
      },

      getPaletteColors: (id) => {
        const palette = get().getPaletteById(id);
        return palette?.attributes.colors;
      },

      reset: () => {
        set(initialState);
      },
    }))
  )
);

// Selectors
export const paletteSelectors = {
  palettes: (state: PaletteStore) => state.palettes,
  isLoading: (state: PaletteStore) => state.isLoading,
  error: (state: PaletteStore) => state.error,
  isInitialized: (state: PaletteStore) => state.isInitialized,
  // palettes are already sorted in setPalettes action
  sortedPalettes: (state: PaletteStore) => state.palettes,
};
